import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { clientiAPI } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Users,
  Loader2,
  Building2,
  Mail,
  Phone,
} from 'lucide-react';

const emptyFormData = {
  ragione_sociale: '',
  email: '',
  indirizzo: '',
  citta: '',
  cap: '',
  codice_fiscale: '',
  partita_iva: '',
  telefono: '',
  pec: '',
  riferimento: '',
  codice_sdi: '',
};

const Clienti = () => {
  const [clienti, setClienti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [formData, setFormData] = useState(emptyFormData);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchClienti();
  }, []);

  const fetchClienti = async () => {
    try {
      const response = await clientiAPI.list();
      setClienti(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei clienti');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (cliente = null) => {
    if (cliente) {
      setSelectedCliente(cliente);
      setFormData({
        ragione_sociale: cliente.ragione_sociale || '',
        email: cliente.email || '',
        indirizzo: cliente.indirizzo || '',
        citta: cliente.citta || '',
        cap: cliente.cap || '',
        codice_fiscale: cliente.codice_fiscale || '',
        partita_iva: cliente.partita_iva || '',
        telefono: cliente.telefono || '',
        pec: cliente.pec || '',
        riferimento: cliente.riferimento || '',
        codice_sdi: cliente.codice_sdi || '',
      });
    } else {
      setSelectedCliente(null);
      setFormData(emptyFormData);
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (selectedCliente) {
        await clientiAPI.update(selectedCliente.id, formData);
        toast.success('Cliente aggiornato');
      } else {
        await clientiAPI.create(formData);
        toast.success('Cliente creato');
      }
      setDialogOpen(false);
      fetchClienti();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await clientiAPI.delete(selectedCliente.id);
      toast.success('Cliente eliminato');
      setDeleteDialogOpen(false);
      fetchClienti();
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  const filteredClienti = clienti.filter((cliente) =>
    cliente.ragione_sociale.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cliente.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cliente.partita_iva?.includes(searchTerm)
  );

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-96" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="clienti-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-heading font-bold text-slate-900">Clienti</h1>
            <p className="text-slate-500 mt-1">Gestisci la tua anagrafica clienti</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={() => handleOpenDialog()}
                data-testid="new-cliente-btn"
                className="bg-brand-orange hover:bg-brand-orange-dark text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuovo Cliente
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading">
                  {selectedCliente ? 'Modifica Cliente' : 'Nuovo Cliente'}
                </DialogTitle>
                <DialogDescription>
                  Inserisci i dati del cliente
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="ragione_sociale">Ragione Sociale *</Label>
                    <Input
                      id="ragione_sociale"
                      value={formData.ragione_sociale}
                      onChange={(e) => setFormData({ ...formData, ragione_sociale: e.target.value })}
                      required
                      data-testid="cliente-ragione-sociale"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      data-testid="cliente-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pec">PEC</Label>
                    <Input
                      id="pec"
                      type="email"
                      value={formData.pec}
                      onChange={(e) => setFormData({ ...formData, pec: e.target.value })}
                      data-testid="cliente-pec"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="telefono">Telefono</Label>
                    <Input
                      id="telefono"
                      value={formData.telefono}
                      onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                      data-testid="cliente-telefono"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="riferimento">Persona di Riferimento</Label>
                    <Input
                      id="riferimento"
                      value={formData.riferimento}
                      onChange={(e) => setFormData({ ...formData, riferimento: e.target.value })}
                      data-testid="cliente-riferimento"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="indirizzo">Indirizzo</Label>
                    <Input
                      id="indirizzo"
                      value={formData.indirizzo}
                      onChange={(e) => setFormData({ ...formData, indirizzo: e.target.value })}
                      data-testid="cliente-indirizzo"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="citta">Città</Label>
                    <Input
                      id="citta"
                      value={formData.citta}
                      onChange={(e) => setFormData({ ...formData, citta: e.target.value })}
                      data-testid="cliente-citta"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cap">CAP</Label>
                    <Input
                      id="cap"
                      value={formData.cap}
                      onChange={(e) => setFormData({ ...formData, cap: e.target.value })}
                      data-testid="cliente-cap"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="partita_iva">Partita IVA</Label>
                    <Input
                      id="partita_iva"
                      value={formData.partita_iva}
                      onChange={(e) => setFormData({ ...formData, partita_iva: e.target.value })}
                      data-testid="cliente-partita-iva"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="codice_fiscale">Codice Fiscale</Label>
                    <Input
                      id="codice_fiscale"
                      value={formData.codice_fiscale}
                      onChange={(e) => setFormData({ ...formData, codice_fiscale: e.target.value })}
                      data-testid="cliente-codice-fiscale"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="codice_sdi">Codice SDI</Label>
                    <Input
                      id="codice_sdi"
                      value={formData.codice_sdi}
                      onChange={(e) => setFormData({ ...formData, codice_sdi: e.target.value })}
                      data-testid="cliente-codice-sdi"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                  >
                    Annulla
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving}
                    data-testid="cliente-submit"
                    className="bg-brand-orange hover:bg-brand-orange-dark text-white"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Salvataggio...
                      </>
                    ) : selectedCliente ? (
                      'Aggiorna'
                    ) : (
                      'Crea Cliente'
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Cerca per ragione sociale, email o P.IVA..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="clienti-search"
          />
        </div>

        {/* Table */}
        <Card className="border-slate-200">
          <CardContent className="p-0">
            {filteredClienti.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold">Ragione Sociale</TableHead>
                    <TableHead className="font-semibold">Email</TableHead>
                    <TableHead className="font-semibold">Telefono</TableHead>
                    <TableHead className="font-semibold">P.IVA</TableHead>
                    <TableHead className="font-semibold">Città</TableHead>
                    <TableHead className="text-right font-semibold">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredClienti.map((cliente) => (
                    <TableRow key={cliente.id} className="table-row-hover">
                      <TableCell className="font-medium">{cliente.ragione_sociale}</TableCell>
                      <TableCell>{cliente.email || '-'}</TableCell>
                      <TableCell>{cliente.telefono || '-'}</TableCell>
                      <TableCell className="font-mono text-sm">{cliente.partita_iva || '-'}</TableCell>
                      <TableCell>{cliente.citta || '-'}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenDialog(cliente)}
                            data-testid={`edit-cliente-${cliente.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedCliente(cliente);
                              setDeleteDialogOpen(true);
                            }}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-cliente-${cliente.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-16">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 mb-4">
                  {searchTerm ? 'Nessun cliente trovato' : 'Nessun cliente ancora'}
                </p>
                {!searchTerm && (
                  <Button variant="outline" onClick={() => handleOpenDialog()}>
                    <Plus className="w-4 h-4 mr-2" />
                    Aggiungi il primo cliente
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Delete Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Elimina Cliente</AlertDialogTitle>
              <AlertDialogDescription>
                Sei sicuro di voler eliminare "{selectedCliente?.ragione_sociale}"? Questa azione non può essere annullata.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annulla</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                Elimina
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default Clienti;
